import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Download, PackageOpen } from 'lucide-react';
import type { BeneficiaryPackageImportResult, BeneficiaryPackagePreview, PackageImportAction, PackageImportDecision } from '@mnc/contracts';
import { api } from '../../lib/api';
import { describeError } from '../../lib/errors';
import { pick } from '../../i18n';
import { Field, Modal, SearchableSelect, cn } from '../../components/ui';

/** Hand-over of one or more individuals as a package (phase-3b-scope): options, then the download. */
export function ExportPackageDialog({ ids, title, onClose }: { ids: string[]; title?: string; onClose: () => void }) {
  const { t } = useTranslation();
  const [attachments, setAttachments] = useState(true);
  const [password, setPassword] = useState('');
  const run = useMutation({
    mutationFn: () => api.download('/api/beneficiaries/packages', { ids, include_attachments: attachments, password: password || undefined }),
    onSuccess: () => { toast.success(t('beneficiaries.package.downloaded')); onClose(); },
    onError: (e) => toast.error(describeError(e, t)),
  });
  const passwordOk = password.length === 0 || password.length >= 8;
  return (
    <Modal open onClose={onClose} title={t('beneficiaries.package.exportTitle')} footer={<><button type="button" className="btn-secondary" onClick={onClose}>{t('app.cancel')}</button><button type="button" className="btn-primary" disabled={run.isPending || !passwordOk} onClick={() => run.mutate()}><Download size={16} /> {t('beneficiaries.package.download')}</button></>}>
      <div className="space-y-3 text-sm">
        {title && <p className="font-medium" dir="auto">{title}</p>}
        <p className="text-neutral-600 dark:text-neutral-300">{t('beneficiaries.package.exportHint')}</p>
        <label className="flex items-center gap-2"><input type="checkbox" checked={attachments} onChange={(e) => setAttachments(e.target.checked)} /> {t('beneficiaries.package.includeAttachments')}</label>
        <Field label={t('beneficiaries.package.password')} hint={t('beneficiaries.package.passwordHint')}>
          <input className={cn('input', !passwordOk && 'border-rose-400')} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <p className="text-xs text-neutral-500">{t('beneficiaries.package.validity')}</p>
      </div>
    </Modal>
  );
}

type Decision = { action: PackageImportAction; target_id?: string };

/** Import wizard: pick the file, check the preview and decide per person, run, read the result. */
export function ImportPackageDialog({ onClose, onImported }: { onClose: () => void; onImported: () => void }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState('');
  const [preview, setPreview] = useState<BeneficiaryPackagePreview | null>(null);
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});
  const [result, setResult] = useState<BeneficiaryPackageImportResult | null>(null);
  const form = () => {
    const fd = new FormData();
    fd.append('file', file!);
    if (password) fd.append('password', password);
    return fd;
  };
  const check = useMutation({
    mutationFn: () => api.post<BeneficiaryPackagePreview>('/api/beneficiaries/packages/preview', form()),
    onSuccess: (p) => {
      setPreview(p);
      const d: Record<string, Decision> = {};
      for (const it of p.items) d[it.export_id] = { action: it.suggested, target_id: it.suggested === 'merge' ? (it.duplicates.find((x) => x.field === 'national_id') ?? it.duplicates[0])?.id : undefined };
      setDecisions(d);
    },
    onError: (e) => toast.error(describeError(e, t)),
  });
  const run = useMutation({
    mutationFn: () => {
      const fd = form();
      const items: PackageImportDecision[] = Object.entries(decisions).map(([export_id, d]) => ({ export_id, action: d.action, ...(d.action === 'merge' && d.target_id ? { target_id: d.target_id } : {}) }));
      fd.append('decisions', JSON.stringify({ items }));
      return api.post<BeneficiaryPackageImportResult>('/api/beneficiaries/packages/import', fd);
    },
    onSuccess: (r) => { setResult(r); onImported(); },
    onError: (e) => toast.error(describeError(e, t)),
  });
  const when = (iso: string) => new Date(iso).toLocaleDateString(lang);
  const ready = preview && !preview.expired && Object.values(decisions).every((d) => d.action !== 'merge' || d.target_id);
  const footer = result
    ? <button type="button" className="btn-primary" onClick={onClose}>{t('app.close')}</button>
    : preview
      ? <><button type="button" className="btn-secondary" onClick={() => { setPreview(null); setResult(null); }}>{t('app.back')}</button><button type="button" className="btn-primary" disabled={!ready || run.isPending} onClick={() => run.mutate()}><PackageOpen size={16} /> {t('beneficiaries.package.run')}</button></>
      : <><button type="button" className="btn-secondary" onClick={onClose}>{t('app.cancel')}</button><button type="button" className="btn-primary" disabled={!file || check.isPending} onClick={() => check.mutate()}>{t('beneficiaries.package.preview')}</button></>;

  return (
    <Modal open onClose={onClose} title={t('beneficiaries.package.importTitle')} wide footer={footer}>
      {result ? (
        <div className="space-y-3 text-sm">
          <p className="font-medium">{t('beneficiaries.package.result', { created: result.created, merged: result.merged, skipped: result.skipped })}</p>
          <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {result.items.map((it) => {
              const p = preview?.items.find((x) => x.export_id === it.export_id);
              return (
                <li key={it.export_id} className="flex flex-wrap items-start justify-between gap-2 py-2">
                  <span dir="auto">{p ? pick(lang, p.name_en, p.name_ar) : it.export_id} <span className="text-xs text-neutral-500">· {t(`beneficiaries.package.actions.${it.action}`)}{it.id ? ` · ${it.sections_added} / ${it.attachments_added}` : ''}</span>
                    {it.notes.length > 0 && <div className="text-xs text-amber-700 dark:text-amber-300">{t('beneficiaries.package.notes')}: {it.notes.join('; ')}</div>}
                  </span>
                  {it.id && <a className="btn-secondary px-2 py-1 text-xs" href={`/beneficiaries/${it.id}`}>{t('beneficiaries.package.open')}</a>}
                </li>
              );
            })}
          </ul>
        </div>
      ) : preview ? (
        <div className="space-y-3 text-sm">
          <p className="text-neutral-600 dark:text-neutral-300">{t('beneficiaries.package.from', { entity: pick(lang, preview.source.name_en, preview.source.name_ar) || preview.source.slug, who: pick(lang, preview.exported_by.display_name_en, preview.exported_by.display_name_ar), date: when(preview.exported_at), until: when(preview.expires_at) })}</p>
          {preview.expired && <p className="rounded-lg bg-rose-50 p-2 text-rose-700 dark:bg-rose-900/30 dark:text-rose-200">{t('beneficiaries.package.expired')}</p>}
          {preview.sections_off.length > 0 && <p className="rounded-lg bg-amber-50 p-2 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">{t('beneficiaries.package.sectionsOff', { sections: preview.sections_off.map((s) => t(`beneficiaries.sectionTitles.${s}`)).join(', ') })}</p>}
          <table className="w-full text-sm">
            <thead><tr className="text-start text-xs text-neutral-500"><th className="py-1 text-start">{t('beneficiaries.package.person')}</th><th className="py-1 text-start">{t('beneficiaries.package.matches')}</th><th className="py-1 text-start">{t('beneficiaries.package.decision')}</th></tr></thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {preview.items.map((it) => {
                const d = decisions[it.export_id] ?? { action: 'skip' as const };
                return (
                  <tr key={it.export_id} className="align-top">
                    <td className="py-2 pe-3">
                      <div className="font-medium" dir="auto">{pick(lang, it.name_en, it.name_ar)}{it.national_id_hint && <span className="ms-1 text-xs text-neutral-500" dir="ltr">{t('beneficiaries.package.idEnding', { hint: it.national_id_hint })}</span>}</div>
                      <div className="text-xs text-neutral-500">{t('beneficiaries.package.counts', { education: it.counts.education, work: it.counts.work_experience, skills: it.counts.skills, certs: it.counts.certifications, addresses: it.counts.addresses, files: it.counts.attachments })}</div>
                      {it.unknown_types.length > 0 && <div className="text-xs text-amber-700 dark:text-amber-300">{t('beneficiaries.package.unknownTypes', { types: it.unknown_types.map((x) => pick(lang, x.en, x.ar)).join(', ') })}</div>}
                      {!it.region_known && <div className="text-xs text-amber-700 dark:text-amber-300">{t('beneficiaries.package.regionUnknown')}</div>}
                    </td>
                    <td className="py-2 pe-3">
                      {it.duplicates.length === 0 ? <span className="text-neutral-500">{t('beneficiaries.package.noMatches')}</span> : (
                        <ul className="space-y-0.5">{it.duplicates.map((m) => <li key={`${m.id}-${m.field}`} dir="auto">{pick(lang, m.name_en, m.name_ar)} <span className="text-xs text-neutral-500">· {t(`beneficiaries.duplicateFields.${m.field}`, { defaultValue: m.field })}</span></li>)}</ul>
                      )}
                    </td>
                    <td className="py-2">
                      <div className="w-40"><SearchableSelect value={d.action} onChange={(v) => setDecisions({ ...decisions, [it.export_id]: { action: v as PackageImportAction, target_id: v === 'merge' ? (d.target_id ?? it.duplicates[0]?.id) : undefined } })} options={(['create', 'merge', 'skip'] as const).filter((a) => a !== 'merge' || it.duplicates.length > 0).map((a) => ({ value: a, label: t(`beneficiaries.package.actions.${a}`) }))} /></div>
                      {d.action === 'merge' && <div className="mt-1 w-40"><SearchableSelect value={d.target_id ?? ''} onChange={(v) => setDecisions({ ...decisions, [it.export_id]: { action: 'merge', target_id: (v as string) || undefined } })} options={it.duplicates.filter((m, i, arr) => arr.findIndex((x) => x.id === m.id) === i).map((m) => ({ value: m.id, label: pick(lang, m.name_en, m.name_ar) }))} placeholder={t('beneficiaries.package.target')} /></div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="space-y-3 text-sm">
          <p className="text-neutral-600 dark:text-neutral-300">{t('beneficiaries.package.importHint')}</p>
          <Field label={t('beneficiaries.package.file')} required><input type="file" accept=".zip,.mncpkg" onChange={(e) => setFile(e.target.files?.[0] ?? null)} /></Field>
          <Field label={t('beneficiaries.package.passwordImport')}><input className="input" type="password" autoComplete="off" value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
        </div>
      )}
    </Modal>
  );
}
